/**
 * Vägvalsskärm före inloggningen på telefon: Field eller skrivbordsversionen.
 * Mockup: docs/visions/field-vagval/field-vagval-mockup.html (flik A).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sparaVersionsVal, VAL_FIELD, VAL_SKRIVBORD } from '../../utils/versionsVal';
import './VersionsVal.css';

const IkonTelefon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" />
  </svg>
);

const IkonDator = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" />
  </svg>
);

const Chevron = ({ className }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

export default function VersionsVal({ onSkrivbord }) {
  const navigate = useNavigate();
  const [komIhag, setKomIhag] = useState(true);
  const [tipsOppet, setTipsOppet] = useState(false);

  const valjField = () => {
    if (komIhag) sparaVersionsVal(VAL_FIELD);
    navigate('/field', { replace: true });
  };

  const valjSkrivbord = () => {
    if (komIhag) sparaVersionsVal(VAL_SKRIVBORD);
    onSkrivbord();
  };

  return (
    <div className="kp-vv">
      <div className="kp-vv__inner">
        <div className="kp-vv__logo">
          <span className="kp-vv__logo-mark">
            <svg viewBox="0 0 12 12" width="16" height="16" aria-hidden="true"><path d="M2 5h3v5H2V5zm5-3h3v8H7V2z" fill="#fff" /></svg>
          </span>
          <span className="kp-vv__logo-text">KanPlan</span>
        </div>

        <h1 className="kp-vv__title">Hur vill du använda KanPlan?</h1>
        <p className="kp-vv__lead">
          Du verkar använda en mobil. Field är byggd för telefonen — skrivbordsversionen fungerar bäst på dator.
        </p>

        <button type="button" className="kp-vv__choice kp-vv__choice--primary" onClick={valjField}>
          <span className="kp-vv__choice-icon"><IkonTelefon /></span>
          <span className="kp-vv__choice-body">
            <span className="kp-vv__choice-title">
              KanPlan Field <span className="kp-vv__badge">Rekommenderas</span>
            </span>
            <span className="kp-vv__choice-desc">
              Tidrapport, dagbok, egenkontroller, foton och utlägg — direkt på arbetsplatsen.
            </span>
          </span>
          <Chevron className="kp-vv__chev" />
        </button>

        <button type="button" className="kp-vv__choice" onClick={valjSkrivbord}>
          <span className="kp-vv__choice-icon"><IkonDator /></span>
          <span className="kp-vv__choice-body">
            <span className="kp-vv__choice-title">Skrivbordsversionen</span>
            <span className="kp-vv__choice-desc">Hela KanPlan med alla moduler. Bäst på dator eller surfplatta.</span>
          </span>
          <Chevron className="kp-vv__chev" />
        </button>

        <label className="kp-vv__remember">
          <input
            type="checkbox"
            className="kp-vv__cb"
            checked={komIhag}
            onChange={(e) => setKomIhag(e.target.checked)}
          />
          Kom ihåg mitt val på den här telefonen
        </label>

        <div className={`kp-vv__tip${tipsOppet ? ' is-open' : ''}`}>
          <button
            type="button"
            className="kp-vv__tip-head"
            aria-expanded={tipsOppet}
            onClick={() => setTipsOppet((v) => !v)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="kp-vv__tip-ic" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
            </svg>
            Lägg Field på hemskärmen
            <svg className="kp-vv__tip-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          {tipsOppet && (
            <div className="kp-vv__tip-body">
              <h2 className="kp-vv__tip-h">iPhone (Safari)</h2>
              <ol>
                <li>Öppna Field.</li>
                <li>Tryck på dela-knappen längst ned.</li>
                <li>Välj "Lägg till på hemskärmen".</li>
              </ol>
              <h2 className="kp-vv__tip-h">Android (Chrome)</h2>
              <ol>
                <li>Öppna Field.</li>
                <li>Tryck på menyn med tre prickar.</li>
                <li>Välj "Lägg till på startskärmen".</li>
              </ol>
            </div>
          )}
        </div>

        <p className="kp-vv__foot">Du kan byta när som helst — länken finns i menyn i båda versionerna.</p>
      </div>
    </div>
  );
}

/** Diskret rad i inloggningssidans sidfot på dator. */
export function FieldTipsRad() {
  return (
    <span className="kp-vv-tipsrad">
      <IkonTelefon size={14} />
      Ute på arbetsplatsen? Öppna <strong>{typeof window !== 'undefined' ? window.location.host : 'app.kanplan.se'}/field</strong> i mobilen.
    </span>
  );
}
